import React, { useEffect, useMemo, useState } from "react";
import { api, formatErr } from "../../api";
import MgmtLayout from "../MgmtLayout";
import { Plus, Trash2, Upload, FileSpreadsheet, Loader2, Check as CheckIcon, X, Calendar, TrendingUp } from "lucide-react";
import BankRecon from "./BankRecon";
import DessertsBreakdown from "./DessertsBreakdown";
import DrinksBreakdown from "./DrinksBreakdown";
import PastryBreakdown from "./PastryBreakdown";

const PAYMENTS = ["Cash", "Card", "QR", "E-wallet", "Grab"];
const CHANNELS = ["Walk-in", "GrabFood", "FoodPanda", "ShopeeFood", "Delivery"];
const inputCls = "bg-cream border border-ink/15 rounded-sm px-3 py-2 focus:outline-none focus:border-matcha-dark transition-colors text-ink text-sm";
const fmt = (v) => `RM ${Number(v || 0).toFixed(2)}`;

function nowLocalISO() {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
}

export default function MgmtSales() {
  const [tab, setTab] = useState("quick");
  const [products, setProducts] = useState([]);
  useEffect(() => {
    api.get("/mgmt/products").then((r) => setProducts(r.data));
  }, []);
  return (
    <MgmtLayout title="Sales & Orders" subtitle="— Daily sales entry">
      <div className="flex items-center gap-6 border-b border-ink/15 mb-8">
        {[
          { k: "quick", label: "Quick Daily Total" },
          { k: "entry", label: "Log a Sale (itemised)" },
          { k: "import", label: "Import from POS" },
          { k: "bank", label: "Bank Recon" },
          { k: "drinks", label: "Drinks" },
          { k: "desserts", label: "Desserts" },
          { k: "pastry", label: "Pastry" },
          { k: "history", label: "Order History" },
        ].map((t) => (
          <button
            key={t.k}
            data-testid={`sales-tab-${t.k}`}
            onClick={() => setTab(t.k)}
            className={`pb-3 -mb-px overline transition-colors ${
              tab === t.k ? "text-matcha-dark border-b-2 border-matcha-dark" : "text-ink-soft hover:text-ink"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>
      {tab === "quick" && <QuickTotal />}
      {tab === "entry" && <SaleEntry products={products} />}
      {tab === "import" && <PosImport products={products} />}
      {tab === "bank" && <BankRecon />}
      {tab === "drinks" && <DrinksBreakdown />}
      {tab === "desserts" && <DessertsBreakdown />}
      {tab === "pastry" && <PastryBreakdown />}
      {tab === "history" && <OrderHistory products={products} />}
    </MgmtLayout>
  );
}

function SaleEntry({ products }) {
  const [when, setWhen] = useState(nowLocalISO());
  const [lines, setLines] = useState([{ product_id: "", qty: 1, unit_price: "", discount: 0, promotion: "" }]);
  const [discountTotal, setDiscountTotal] = useState(0);
  const [payment, setPayment] = useState("Cash");
  const [channel, setChannel] = useState("Walk-in");
  const [notes, setNotes] = useState("");
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const total = useMemo(() => {
    let t = 0;
    for (const l of lines) {
      const p = products.find((x) => x.id === l.product_id);
      const unit = l.unit_price !== "" ? Number(l.unit_price) : (p?.selling_price || 0);
      t += unit * Number(l.qty || 0) - Number(l.discount || 0);
    }
    return Math.max(0, t - Number(discountTotal || 0));
  }, [lines, products, discountTotal]);

  const updateLine = (i, k, v) => setLines((s) => s.map((l, idx) => idx === i ? { ...l, [k]: v } : l));
  const addLine = () => setLines((s) => [...s, { product_id: "", qty: 1, unit_price: "", discount: 0, promotion: "" }]);
  const delLine = (i) => setLines((s) => s.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setMsg(""); setBusy(true);
    try {
      const items = lines
        .filter((l) => l.product_id && Number(l.qty) > 0)
        .map((l) => ({
          product_id: l.product_id,
          qty: Number(l.qty),
          unit_price: l.unit_price === "" ? null : Number(l.unit_price),
          discount: Number(l.discount || 0),
          promotion: l.promotion || "",
        }));
      if (!items.length) throw new Error("Add at least one item.");
      const isoUTC = new Date(when).toISOString();
      await api.post("/mgmt/sales", {
        datetime: isoUTC,
        items,
        discount_total: Number(discountTotal || 0),
        payment_method: payment,
        channel,
        notes,
      });
      setMsg("Sale recorded. Inventory updated.");
      setLines([{ product_id: "", qty: 1, unit_price: "", discount: 0, promotion: "" }]);
      setDiscountTotal(0);
      setNotes("");
    } catch (e2) {
      setErr(formatErr(e2?.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="sale-form" className="bg-bone border border-ink/10 rounded-sm p-6 md:p-8 max-w-5xl">
      <div className="grid md:grid-cols-3 gap-4 mb-6">
        <div>
          <label className="overline text-ink-soft block mb-2">Date & Time</label>
          <input data-testid="sale-datetime" type="datetime-local" className={inputCls + " w-full"} value={when} onChange={(e) => setWhen(e.target.value)} required />
        </div>
        <div>
          <label className="overline text-ink-soft block mb-2">Payment</label>
          <select data-testid="sale-payment" className={inputCls + " w-full"} value={payment} onChange={(e) => setPayment(e.target.value)}>
            {PAYMENTS.map((p) => <option key={p}>{p}</option>)}
          </select>
        </div>
        <div>
          <label className="overline text-ink-soft block mb-2">Channel</label>
          <select data-testid="sale-channel" className={inputCls + " w-full"} value={channel} onChange={(e) => setChannel(e.target.value)}>
            {CHANNELS.map((c) => <option key={c}>{c}</option>)}
          </select>
        </div>
      </div>

      <p className="overline text-matcha-dark mb-3">— Items</p>
      <div className="space-y-3">
        {lines.map((l, i) => (
          <div key={i} className="grid grid-cols-12 gap-2 items-end" data-testid={`sale-line-${i}`}>
            <div className="col-span-12 md:col-span-5">
              <label className="overline text-ink-soft block mb-1 text-[0.62rem]">Product</label>
              <select data-testid={`sale-line-product-${i}`} className={inputCls + " w-full"} value={l.product_id} onChange={(e) => updateLine(i, "product_id", e.target.value)}>
                <option value="">— Select —</option>
                {products.map((p) => <option key={p.id} value={p.id}>{p.name} · {fmt(p.selling_price)}</option>)}
              </select>
            </div>
            <div className="col-span-3 md:col-span-2">
              <label className="overline text-ink-soft block mb-1 text-[0.62rem]">Qty</label>
              <input data-testid={`sale-line-qty-${i}`} type="number" min="1" className={inputCls + " w-full"} value={l.qty} onChange={(e) => updateLine(i, "qty", e.target.value)} />
            </div>
            <div className="col-span-4 md:col-span-2">
              <label className="overline text-ink-soft block mb-1 text-[0.62rem]">Unit price</label>
              <input type="number" step="0.01" placeholder="auto" className={inputCls + " w-full"} value={l.unit_price} onChange={(e) => updateLine(i, "unit_price", e.target.value)} />
            </div>
            <div className="col-span-4 md:col-span-2">
              <label className="overline text-ink-soft block mb-1 text-[0.62rem]">Line discount</label>
              <input type="number" step="0.01" className={inputCls + " w-full"} value={l.discount} onChange={(e) => updateLine(i, "discount", e.target.value)} />
            </div>
            <div className="col-span-1">
              <button type="button" onClick={() => delLine(i)} className="text-ink-soft hover:text-red-600 pb-2.5">
                <Trash2 size={16} />
              </button>
            </div>
          </div>
        ))}
      </div>
      <button type="button" data-testid="sale-add-line" onClick={addLine} className="mt-3 inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-matcha-dark hover:text-ink">
        <Plus size={14} /> Add item
      </button>

      <div className="grid md:grid-cols-3 gap-4 mt-8">
        <div>
          <label className="overline text-ink-soft block mb-2">Order-level discount (RM)</label>
          <input data-testid="sale-discount sale-discount-total" type="number" step="0.01" className={inputCls + " w-full"} value={discountTotal} onChange={(e) => setDiscountTotal(e.target.value)} />
        </div>
        <div className="md:col-span-2">
          <label className="overline text-ink-soft block mb-2">Notes (promotion code, voucher, etc.)</label>
          <input data-testid="sale-notes" className={inputCls + " w-full"} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </div>
      </div>

      {err && <p data-testid="sale-error" className="text-sm text-red-700 bg-red-50 px-3 py-2 rounded-sm mt-6">{err}</p>}
      {msg && <p data-testid="sale-success" className="text-sm text-matcha-dark mt-6">{msg}</p>}

      <div className="mt-8 flex items-center justify-between flex-wrap gap-4">
        <p className="font-display text-2xl text-ink" style={{ letterSpacing: "0.04em" }}>
          Total · <span className="text-matcha-dark">{fmt(total)}</span>
        </p>
        <button data-testid="sale-submit" disabled={busy} className="rounded-full px-7 py-3 bg-ink text-bone text-xs uppercase tracking-[0.22em] hover:bg-matcha-dark transition-colors disabled:opacity-50">
          {busy ? "Saving…" : "Record Sale"}
        </button>
      </div>
    </form>
  );
}

function OrderHistory({ products }) {
  const [rows, setRows] = useState([]);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [channel, setChannel] = useState("");
  const [payment, setPayment] = useState("");
  const [productId, setProductId] = useState("");

  const load = async () => {
    const params = new URLSearchParams();
    if (from) params.set("date_from", from);
    if (to) params.set("date_to", to);
    if (channel) params.set("channel", channel);
    if (payment) params.set("payment_method", payment);
    if (productId) params.set("product_id", productId);
    const { data } = await api.get(`/mgmt/sales?${params.toString()}`);
    setRows(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [from, to, channel, payment, productId]);

  const del = async (id) => {
    if (!window.confirm("Delete this sale and restore inventory?")) return;
    await api.delete(`/mgmt/sales/${id}`);
    load();
  };

  const totals = useMemo(() => ({
    revenue: rows.reduce((s, r) => s + r.total, 0),
    gp: rows.reduce((s, r) => s + r.gross_profit, 0),
    orders: rows.length,
  }), [rows]);

  return (
    <div>
      <div className="bg-bone border border-ink/10 rounded-sm p-5 mb-6 grid md:grid-cols-5 gap-4">
        <div><label className="overline text-ink-soft block mb-1 text-[0.62rem]">From</label><input data-testid="orders-from filter-date-from" type="date" className={inputCls + " w-full"} value={from} onChange={(e) => setFrom(e.target.value)} /></div>
        <div><label className="overline text-ink-soft block mb-1 text-[0.62rem]">To</label><input data-testid="orders-to filter-date-to" type="date" className={inputCls + " w-full"} value={to} onChange={(e) => setTo(e.target.value)} /></div>
        <div><label className="overline text-ink-soft block mb-1 text-[0.62rem]">Channel</label>
          <select data-testid="orders-channel filter-channel" className={inputCls + " w-full"} value={channel} onChange={(e) => setChannel(e.target.value)}>
            <option value="">Any</option>{CHANNELS.map((c) => <option key={c}>{c}</option>)}
          </select>
        </div>
        <div><label className="overline text-ink-soft block mb-1 text-[0.62rem]">Payment</label>
          <select data-testid="orders-payment filter-payment" className={inputCls + " w-full"} value={payment} onChange={(e) => setPayment(e.target.value)}>
            <option value="">Any</option>{PAYMENTS.map((p) => <option key={p}>{p}</option>)}
          </select>
        </div>
        <div><label className="overline text-ink-soft block mb-1 text-[0.62rem]">Product</label>
          <select className={inputCls + " w-full"} value={productId} onChange={(e) => setProductId(e.target.value)}>
            <option value="">Any</option>{products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-6">
        <Kpi label="Filtered Orders" v={totals.orders} testid="filtered-orders" />
        <Kpi label="Revenue" v={fmt(totals.revenue)} testid="filtered-revenue" />
        <Kpi label="Gross Profit" v={fmt(totals.gp)} testid="filtered-gp" />
      </div>

      <div className="bg-bone border border-ink/10 rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-sage/40">
            <tr className="text-left text-ink-soft">
              <th className="px-4 py-3 overline">Date/Time</th>
              <th className="px-4 py-3 overline">Items</th>
              <th className="px-4 py-3 overline">Channel</th>
              <th className="px-4 py-3 overline">Payment</th>
              <th className="px-4 py-3 overline text-right">Total</th>
              <th className="px-4 py-3 overline text-right">GP</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-ink/5 align-top" data-testid={`order-row-${r.id}`}>
                <td className="px-4 py-3 whitespace-nowrap">{r.datetime?.slice(0, 16).replace("T", " ")}</td>
                <td className="px-4 py-3">
                  {r.items.map((it, k) => (
                    <div key={k} className="text-ink-soft">{it.qty} × {it.name}</div>
                  ))}
                </td>
                <td className="px-4 py-3 text-ink-soft">{r.channel}</td>
                <td className="px-4 py-3 text-ink-soft">{r.payment_method}</td>
                <td className="px-4 py-3 text-right text-ink">{fmt(r.total)}</td>
                <td className="px-4 py-3 text-right text-matcha-dark">{fmt(r.gross_profit)}</td>
                <td className="px-4 py-3 text-right">
                  <button data-testid={`order-del-${r.id}`} onClick={() => del(r.id)} className="text-ink-soft hover:text-red-600"><Trash2 size={16} /></button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-ink-soft">No orders.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Kpi({ label, v, testid }) {
  return (
    <div className="bg-bone border border-ink/10 rounded-sm p-4" data-testid={testid}>
      <p className="overline text-ink-soft">{label}</p>
      <p className="font-display text-2xl text-ink mt-1" style={{ letterSpacing: "0.02em" }}>{v}</p>
    </div>
  );
}

function QuickTotal() {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [total, setTotal] = useState("");
  const [orders, setOrders] = useState("");
  const [cogs, setCogs] = useState("");
  const [payment, setPayment] = useState("Mixed");
  const [channel, setChannel] = useState("Mixed");
  const [notes, setNotes] = useState("");
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [existing, setExisting] = useState(null);

  // Pre-fill from any existing quick-entry sale for the selected date (so admin sees
  // the current values before overwriting). Uses date_from/date_to range filter.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/mgmt/sales", { params: { date_from: date, date_to: date } });
        const list = Array.isArray(data) ? data : [];
        const q = list.find((s) => s.quick_entry && s.date === date) || null;
        if (cancelled) return;
        setExisting(q);
        if (q) {
          setTotal(String(q.total ?? ""));
          setOrders(String(q.order_count ?? ""));
          setCogs(String(q.cogs ?? ""));
          setPayment(q.payment_method || "Mixed");
          setChannel(q.channel || "Mixed");
          setNotes(q.notes || "");
        } else {
          setTotal(""); setOrders(""); setCogs(""); setNotes("");
          setPayment("Mixed"); setChannel("Mixed");
        }
      } catch { /* ignore load errors */ }
    })();
    return () => { cancelled = true; };
  }, [date]);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setMsg(""); setBusy(true);
    try {
      const { data } = await api.post("/mgmt/sales/quick", {
        date,
        total: Number(total),
        orders: Number(orders || 1),
        cogs: Number(cogs || 0),
        payment_method: payment,
        channel,
        notes,
      });
      const action = data?.overwritten ? "Overwrote" : "Recorded";
      setMsg(`${action} daily total of ${fmt(Number(total))} for ${date} (${Number(orders || 1)} orders).`);
      setExisting(data);
    } catch (e2) {
      setErr(formatErr(e2?.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="quick-total-form" className="bg-bone border border-ink/10 rounded-sm p-6 md:p-8 max-w-3xl">
      <p className="overline text-matcha-dark mb-2">— Just the daily total</p>
      <p className="text-ink-soft text-sm mb-6 leading-relaxed">
        Use this when you only need to record a day&apos;s total revenue (e.g. from a POS daily-Z report) without entering each order.
        No inventory will be deducted. For ingredient/COGS tracking, use the itemised tab.
      </p>
      {existing && (
        <div className="mb-6 bg-sage/30 border border-matcha-dark/30 rounded-sm px-4 py-3 flex items-center gap-3" data-testid="quick-existing-banner">
          <span className="overline text-matcha-dark text-[10px]">Existing record</span>
          <span className="text-sm text-ink">
            RM {Number(existing.total || 0).toFixed(2)} · {existing.order_count || 1} orders
          </span>
          <span className="text-[10px] text-ink-soft ml-auto">Saving again will overwrite it.</span>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="overline text-ink-soft block mb-2">Date</label>
          <input data-testid="quick-date" type="date" required className={inputCls + " w-full"} value={date} onChange={(e) => setDate(e.target.value)} />
        </div>
        <div>
          <label className="overline text-ink-soft block mb-2">Total revenue (RM)</label>
          <input data-testid="quick-total" type="number" step="0.01" min="0" required className={inputCls + " w-full"} value={total} onChange={(e) => setTotal(e.target.value)} placeholder="e.g. 1850.00" />
        </div>
        <div>
          <label className="overline text-ink-soft block mb-2">Number of orders (optional)</label>
          <input data-testid="quick-orders" type="number" min="1" className={inputCls + " w-full"} value={orders} onChange={(e) => setOrders(e.target.value)} placeholder="e.g. 78" />
        </div>
        <div>
          <label className="overline text-ink-soft block mb-2">Total COGS (optional)</label>
          <input data-testid="quick-cogs" type="number" step="0.01" min="0" className={inputCls + " w-full"} value={cogs} onChange={(e) => setCogs(e.target.value)} placeholder="e.g. 320.00" />
        </div>
        <div>
          <label className="overline text-ink-soft block mb-2">Payment</label>
          <select data-testid="quick-payment" className={inputCls + " w-full"} value={payment} onChange={(e) => setPayment(e.target.value)}>
            <option>Mixed</option>
            {PAYMENTS.map((p) => <option key={p}>{p}</option>)}
          </select>
        </div>
        <div>
          <label className="overline text-ink-soft block mb-2">Channel</label>
          <select data-testid="quick-channel" className={inputCls + " w-full"} value={channel} onChange={(e) => setChannel(e.target.value)}>
            <option>Mixed</option>
            {CHANNELS.map((c) => <option key={c}>{c}</option>)}
          </select>
        </div>
        <div className="md:col-span-2">
          <label className="overline text-ink-soft block mb-2">Notes (optional)</label>
          <input className={inputCls + " w-full"} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </div>
      </div>

      {err && <p data-testid="quick-error" className="text-sm text-red-700 bg-red-50 px-3 py-2 rounded-sm mt-6">{err}</p>}
      {msg && <p data-testid="quick-success" className="text-sm text-matcha-dark mt-6">{msg}</p>}

      <div className="mt-8 flex justify-end">
        <button data-testid="quick-submit" disabled={busy} className="rounded-full px-7 py-3 bg-ink text-bone text-xs uppercase tracking-[0.22em] hover:bg-matcha-dark transition-colors disabled:opacity-50">
          {busy ? "Saving…" : existing ? "Overwrite Daily Total" : "Record Daily Total"}
        </button>
      </div>
    </form>
  );
}

/* ================================================================
   Import from POS — parse GEBME exports (.xlsx). Accepts either or
   both of:
     • "Store Itemized Report"          → item-level rows
     • "Store Payment Received Report"  → payment-method rows
   Commits both as one aggregate sale per date.
   ================================================================ */
function PosImport() {
  const [phase, setPhase] = useState("pick"); // pick | uploading | review | committing | done
  const [itemsData, setItemsData] = useState(null);       // parsed items file
  const [paymentsData, setPaymentsData] = useState(null); // parsed payments file
  const [ordersData, setOrdersData] = useState(null);     // parsed orders overview file
  const [itemsRows, setItemsRows] = useState([]);         // editable copy of items
  const [date, setDate] = useState("");
  const [storeId, setStoreId] = useState("");
  const [storeName, setStoreName] = useState("");
  const [err, setErr] = useState("");
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [lastAutoSave, setLastAutoSave] = useState(null);
  // Refs mirror the state so that concurrent uploadFile() calls (e.g. dropping
  // 4 files at once) see each other's parsed data — otherwise setState-async
  // races would cause each auto-commit to only include its own file's data.
  const itemsRef = React.useRef(null);
  const itemsRowsRef = React.useRef([]);
  const paymentsRef = React.useRef(null);
  const ordersRef = React.useRef(null);
  const dateRef = React.useRef("");
  const storeIdRef = React.useRef("");

  const uploadFile = async (f) => {
    setErr("");
    if (!f) return;
    if (!/\.(xlsx|xls|xlsm)$/i.test(f.name)) { setErr("Please upload an Excel file (.xlsx)."); return; }
    if (f.size > 12 * 1024 * 1024) { setErr("File must be smaller than 12 MB."); return; }
    setUploading(true);
    setPhase("uploading");
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/mgmt/sales/import-pos/preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Route parsed data into the right slot — write refs (sync) and state (async).
      if (data.kind === "items") {
        // GEBME exports two "items" reports for the same day:
        //   • variant="basic"     → Store Itemized Report (qty + sales only)
        //   • variant="breakdown" → Store Category Breakdown (adds unit price + modifiers)
        // If the user drops both, the breakdown is strictly richer, so it wins.
        // Without this, the second file to arrive would blindly overwrite the first
        // and one report would appear to "never upload".
        const rank = (v) => (v === "breakdown" ? 2 : 1);
        const incomingRank = rank(data.variant);
        const existingRank = rank(itemsRef.current?.variant);
        if (itemsRef.current && incomingRank < existingRank) {
          // Keep the richer report we already have; skip this one silently.
          setPhase("review");
          return;
        }
        const rows = (data.items || []).map((r) => ({
          extracted_name: r.extracted_name,
          qty: r.qty,
          revenue: r.revenue,
          unit_price: r.unit_price,
          item_revenue: r.item_revenue,
          modifier_revenue: r.modifier_revenue,
          modifiers: r.modifiers || [],
          match_product_id: r.match_product_id || "",
          match_name: r.match_name || "",
          match_confidence: r.match_confidence || (r.match_product_id ? "exact" : "none"),
          suggested_category: r.suggested_category || "",
          // Auto-tick every row: backend auto-creates a product for any
          // unmatched name (Desserts allow-list first, then keyword classifier,
          // then "Uncategorised") so no sale is ever lost. Admin can untick
          // individual rows to skip; we never silently drop a row just because
          // it didn't have an exact match in the catalog.
          accepted: true,
        }));
        itemsRef.current = data;
        itemsRowsRef.current = rows;
        setItemsData(data);
        setItemsRows(rows);
      } else if (data.kind === "payments") {
        paymentsRef.current = data;
        setPaymentsData(data);
      } else if (data.kind === "orders") {
        ordersRef.current = data;
        setOrdersData(data);
      } else {
        throw new Error("Unrecognised report type.");
      }
      if (data.date) { dateRef.current = data.date; setDate(data.date); }
      if (data.store_id) { storeIdRef.current = data.store_id; setStoreId(data.store_id); }
      if (data.store_name) setStoreName(data.store_name);
      setPhase("review");

      // Auto-commit whenever Payments or Orders are involved (no mapping needed)
      // OR when the Items file has at least one auto-match. Reads from refs so
      // concurrent uploadFile() calls always see the freshest merged snapshot.
      const shouldAutoCommit =
        data.kind === "payments" || data.kind === "orders" ||
        (data.kind === "items" && itemsRowsRef.current.some((r) => r.accepted && (r.match_product_id || (r.extracted_name || "").trim())));
      if (shouldAutoCommit) {
        await commitInternal({
          items: itemsRowsRef.current,
          payments: paymentsRef.current,
          orders: ordersRef.current,
          date: dateRef.current || date,
          storeId: storeIdRef.current || storeId,
          silent: true,
        });
      }
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail) || e.message);
      setPhase(itemsRef.current || paymentsRef.current || ordersRef.current ? "review" : "pick");
    } finally {
      setUploading(false);
    }
  };

  const patchRow = (i, patch) =>
    setItemsRows((s) => s.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  const commitInternal = async ({ items: rowsIn, payments: paymentsIn, orders: ordersIn, date: dateIn, storeId: storeIn, silent = false }) => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dateIn || "")) {
      if (!silent) setErr("Pick a valid date.");
      return;
    }
    const items = (rowsIn || [])
      .filter((r) => r.accepted && Number(r.qty) > 0 && (r.match_product_id || (r.extracted_name || "").trim()))
      .map((r) => ({
        // Backend embeds name + resolved category directly on each sale item
        // WITHOUT inserting into the products catalog. Category is inferred
        // from the strict Desserts allow-list first, then keyword classifier,
        // then "Uncategorised". The catalog stays admin-curated; only the
        // explicit "+ Create as Drink" / "+ Create as Dessert" buttons in
        // the review row add anything to /mgmt/products.
        //
        // `pos_name` always carries the raw POS name so the commit endpoint
        // can learn the alias when the admin's mapping differs from an exact
        // name match — the next upload auto-matches the same short name.
        product_id: r.match_product_id || "",
        name: r.match_product_id ? "" : r.extracted_name,
        pos_name: r.extracted_name || "",
        qty: Number(r.qty),
        unit_price: Number(r.unit_price || 0),
        revenue: Number(r.revenue || 0),
        modifiers: (r.modifiers || []).map((m) => ({
          name: m.name,
          qty: Number(m.qty || 0),
          unit_price: Number(m.unit_price || 0),
          revenue: Number(m.revenue || 0),
        })),
      }));
    const payments = (paymentsIn?.payments || []).map((p) => ({
      method: p.method, raw_method: p.raw_method,
      transactions: Number(p.transactions || 0),
      amount: Number(p.amount || 0),
    }));
    const hourly = (ordersIn?.hourly || []).map((h) => ({
      hour: Number(h.hour),
      dine_in_txns: Number(h.dine_in_txns || 0),
      take_away_txns: Number(h.take_away_txns || 0),
      total_txns: Number(h.total_txns || 0),
      dine_in_amount: Number(h.dine_in_amount || 0),
      take_away_amount: Number(h.take_away_amount || 0),
      total_amount: Number(h.total_amount || 0),
    }));
    if (items.length === 0 && payments.length === 0 && hourly.length === 0) {
      if (!silent) setErr("Upload at least one report and map at least one row.");
      return;
    }
    try {
      const { data } = await api.post("/mgmt/sales/import-pos/commit", {
        date: dateIn, source: "gebme", store_id: storeIn || "",
        items, payments, hourly,
        channels: ordersIn?.channels || null,
        peak_hour: ordersIn?.totals?.peak_hour ?? null,
      });
      setResult(data);
      setLastAutoSave(new Date());
    } catch (e) {
      const msg = formatErr(e?.response?.data?.detail) || e.message;
      if (!silent) setErr(msg);
      throw e;
    }
  };

  const commit = async () => {
    setErr("");
    setPhase("committing");
    try {
      await commitInternal({
        items: itemsRows,
        payments: paymentsData,
        orders: ordersData,
        date, storeId, silent: false,
      });
      setPhase("done");
    } catch {
      setPhase("review");
    }
  };

  const reset = () => {
    setItemsData(null); setPaymentsData(null); setOrdersData(null); setItemsRows([]);
    setResult(null); setDate(""); setStoreId(""); setStoreName("");
    setErr(""); setPhase("pick"); setLastAutoSave(null);
    itemsRef.current = null; itemsRowsRef.current = [];
    paymentsRef.current = null; ordersRef.current = null;
    dateRef.current = ""; storeIdRef.current = "";
  };

  // Create a new product from an unmatched POS row and auto-map this row to it.
  // Admin explicitly picks the category via the button they click (Drink vs
  // Dessert) — no more silent "guess the category" that led to dessert rows
  // landing in "Uncategorised".
  const quickCreateProduct = async (rowIdx, row, forcedCategory = "") => {
    const name = (row.extracted_name || "").trim();
    if (!name) return;
    const category = forcedCategory || row.suggested_category || "Desserts";
    try {
      const { data: created } = await api.post("/mgmt/products", {
        name,
        category,
        selling_price: Number(row.unit_price || 0),
        active: true,
        recipe: [],
      });
      // Add to preview catalog so the dropdown includes the new option.
      setItemsData((d) => d && ({
        ...d,
        products_catalog: [
          ...(d.products_catalog || []),
          { id: created.id, name: created.name, category: created.category, selling_price: created.selling_price },
        ].sort((a, b) => a.name.localeCompare(b.name)),
      }));
      // Auto-match this row.
      patchRow(rowIdx, {
        match_product_id: created.id,
        match_name: created.name,
        suggested_category: "",
        accepted: true,
      });
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail) || e.message || "Could not create product");
    }
  };

  const isRowCommittable = (r) => r.accepted && Number(r.qty) > 0 && (r.match_product_id || (r.extracted_name || "").trim());
  const acceptedCount = itemsRows.filter(isRowCommittable).length;
  const acceptedRevenue = itemsRows
    .filter(isRowCommittable)
    .reduce((s, r) => s + Number(r.revenue || 0), 0);
  const paymentsTotal = (paymentsData?.payments || [])
    .reduce((s, p) => s + Number(p.amount || 0), 0);
  const paymentsTxns = (paymentsData?.payments || [])
    .reduce((s, p) => s + Number(p.transactions || 0), 0);
  const canCommit = (acceptedCount > 0 || (paymentsData?.payments?.length || 0) > 0 || (ordersData?.hourly?.length || 0) > 0);
  const anyLoaded = !!(itemsData || paymentsData || ordersData);
  const hourlyMax = Math.max(1, ...(ordersData?.hourly || []).map((h) => Number(h.total_amount || 0)));

  return (
    <div className="space-y-6" data-testid="pos-import">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="overline text-matcha-dark">POS Sync</p>
          <h2 className="font-display text-2xl text-ink" style={{ letterSpacing: "0.02em" }}>
            Import daily sales from your POS
          </h2>
          <p className="text-ink-soft text-sm mt-1">
            Drag &amp; drop <strong className="text-ink">all four GEBME reports</strong> at once — Itemized, Payment Received,
            Order Overview, and Itemized Breakdown. We&apos;ll detect each one, merge them by date, and save
            the daily total <em className="text-ink">the moment you drop them in</em>.
          </p>
        </div>
        {lastAutoSave && phase !== "done" && (
          <div
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-sage/40 border border-matcha-dark/30 text-matcha-dark text-[11px] uppercase tracking-[0.22em]"
            data-testid="pos-autosave-chip"
            title="Publish never adds anything to your Products & Inventory catalog. Categories are tagged inline on the sale, not on new products. Confirmed mappings are remembered so the next upload auto-matches."
          >
            <CheckIcon size={12} />
            Daily sales saved · {lastAutoSave.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
            <span className="opacity-70 normal-case tracking-normal" data-testid="pos-autosave-no-products">
              · 0 new products
            </span>
            {(result?.learned_aliases?.length || 0) > 0 && (
              <span
                className="opacity-70 normal-case tracking-normal"
                data-testid="pos-autosave-learned-aliases"
                title={result.learned_aliases.map((a) => `${a.alias} → ${a.product_name}`).join("\n")}
              >
                · {result.learned_aliases.length} mapping{result.learned_aliases.length === 1 ? "" : "s"} learned
              </span>
            )}
          </div>
        )}
      </div>

      {(phase === "pick" || phase === "review" || phase === "uploading") && (
        <MultiDropZone
          onFiles={async (files) => { for (const f of files) await uploadFile(f); }}
          uploading={uploading}
          files={[
            itemsData && { kind: "items", name: `Store Itemized${itemsData.variant === "breakdown" ? " · Breakdown" : ""}`,
                           summary: `${itemsData.totals?.qty || 0} units · RM ${Number(itemsData.totals?.revenue || 0).toFixed(2)} · ${itemsData.totals?.matched || 0}/${itemsRows.length} matched${itemsData.totals?.modifier_count ? ` · ${itemsData.totals.modifier_count} mods` : ""}`,
                           onClear: () => { itemsRef.current = null; itemsRowsRef.current = []; setItemsData(null); setItemsRows([]); if (!paymentsData && !ordersData) setPhase("pick"); } },
            paymentsData && { kind: "payments", name: "Payment Received",
                              summary: `${paymentsTxns} txns · RM ${paymentsTotal.toFixed(2)} · ${paymentsData.payments?.length || 0} methods`,
                              onClear: () => { paymentsRef.current = null; setPaymentsData(null); if (!itemsData && !ordersData) setPhase("pick"); } },
            ordersData && { kind: "orders", name: "Daily Order Overview",
                            summary: `${ordersData.totals?.transactions || 0} txns · peak ${String(ordersData.totals?.peak_hour ?? "—").padStart(2,'0')}:00 · ${ordersData.channels?.dine_in?.transactions || 0} dine-in / ${ordersData.channels?.take_away?.transactions || 0} take-away`,
                            onClear: () => { ordersRef.current = null; setOrdersData(null); if (!itemsData && !paymentsData) setPhase("pick"); } },
          ].filter(Boolean)}
        />
      )}

      {err && phase !== "committing" && (
        <p className="text-sm text-red-700 bg-red-50 border border-red-100 px-3 py-2 rounded-sm">{err}</p>
      )}

      {phase === "review" && anyLoaded && (
        <div className="bg-bone border border-ink/10 rounded-sm p-6" data-testid="pos-review">
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-6 pb-4 border-b border-ink/10">
            <Stat label="Store" value={storeName || "—"} sub={storeId ? `ID ${storeId}` : ""} />
            <div>
              <p className="overline text-ink-soft mb-1">Post as date</p>
              <input
                data-testid="pos-date"
                type="date"
                className={inputCls + " w-full"}
                value={date}
                onChange={(e) => setDate(e.target.value)}
              />
            </div>
            <Stat
              label="Items"
              value={itemsData ? `${itemsRows.length} lines` : "—"}
              sub={itemsData ? `${acceptedCount} ready · RM ${acceptedRevenue.toFixed(2)}` : "Not uploaded"}
            />
            <Stat
              label="Orders"
              value={ordersData ? `${ordersData.totals?.transactions || 0} txns` : (paymentsData ? `${paymentsTxns} txns` : "—")}
              sub={ordersData ? `RM ${Number(ordersData.totals?.amount || 0).toFixed(2)} · peak ${String(ordersData.totals?.peak_hour ?? "—").padStart(2,'0')}:00` : (paymentsData ? `RM ${paymentsTotal.toFixed(2)} · ${paymentsData.payments?.length || 0} methods` : "Not uploaded")}
            />
          </div>

          {/* Orders overview: channels + hourly chart */}
          {ordersData && (
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <p className="overline text-ink">Orders overview</p>
                <p className="text-[11px] text-ink-soft uppercase tracking-[0.18em]">
                  <span className="text-ink">{ordersData.channels?.dine_in?.transactions || 0}</span> dine-in ·
                  <span className="text-ink ml-1">{ordersData.channels?.take_away?.transactions || 0}</span> take-away
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="rounded-sm border border-ink/10 bg-bone p-4">
                  <p className="overline text-ink-soft mb-2">Dine-in</p>
                  <p className="text-ink text-xl">{fmt(ordersData.channels?.dine_in?.amount || 0)}</p>
                  <p className="text-[11px] text-ink-soft mt-1">
                    {ordersData.channels?.dine_in?.transactions || 0} txns ·{" "}
                    {ordersData.totals?.amount ? `${((ordersData.channels?.dine_in?.amount / ordersData.totals.amount) * 100).toFixed(1)}%` : "—"}
                  </p>
                </div>
                <div className="rounded-sm border border-ink/10 bg-bone p-4">
                  <p className="overline text-ink-soft mb-2">Take-away</p>
                  <p className="text-ink text-xl">{fmt(ordersData.channels?.take_away?.amount || 0)}</p>
                  <p className="text-[11px] text-ink-soft mt-1">
                    {ordersData.channels?.take_away?.transactions || 0} txns ·{" "}
                    {ordersData.totals?.amount ? `${((ordersData.channels?.take_away?.amount / ordersData.totals.amount) * 100).toFixed(1)}%` : "—"}
                  </p>
                </div>
                <div className="rounded-sm border border-matcha-dark/40 bg-sage/10 p-4">
                  <p className="overline text-matcha-dark mb-2">Peak hour</p>
                  <p className="text-ink text-xl">
                    {String(ordersData.totals?.peak_hour ?? "—").padStart(2,'0')}:00
                  </p>
                  <p className="text-[11px] text-ink-soft mt-1">
                    Busiest of the day
                  </p>
                </div>
              </div>

              {/* Hourly dotted-line chart */}
              <div className="mt-4 rounded-sm border border-ink/10 bg-bone p-4">
                <p className="overline text-ink-soft mb-3">Revenue by hour</p>
                <HourlyLineChart hourly={ordersData.hourly || []} peakHour={ordersData.totals?.peak_hour} />
              </div>
            </div>
          )}

          {/* Payments card */}
          {paymentsData && (
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <p className="overline text-ink">Payments breakdown</p>
                {itemsData && Math.abs(paymentsTotal - (itemsData.totals?.revenue || 0)) > 0.5 && (
                  <p className="text-[11px] text-ink-soft uppercase tracking-[0.18em]">
                    Δ vs items: <span className="text-ink">RM {(paymentsTotal - (itemsData.totals?.revenue || 0)).toFixed(2)}</span>
                    <span className="normal-case text-ink-soft ml-2">(delivery fees / tips / rounding)</span>
                  </p>
                )}
              </div>
              <div className="border border-ink/10 rounded-sm overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-sage/40 text-left text-ink-soft">
                    <tr>
                      <th className="px-3 py-2 overline">Method</th>
                      <th className="px-3 py-2 overline">POS code</th>
                      <th className="px-3 py-2 overline text-right">Transactions</th>
                      <th className="px-3 py-2 overline text-right">Amount</th>
                      <th className="px-3 py-2 overline text-right">Share</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(paymentsData.payments || []).map((p, i) => (
                      <tr key={i} data-testid={`pos-pay-row-${i}`} className="border-t border-ink/5">
                        <td className="px-3 py-2 text-ink">{p.method}</td>
                        <td className="px-3 py-2 text-ink-soft font-mono text-xs">{p.raw_method}</td>
                        <td className="px-3 py-2 text-right">{p.transactions}</td>
                        <td className="px-3 py-2 text-right font-mono">{fmt(p.amount)}</td>
                        <td className="px-3 py-2 text-right text-ink-soft">
                          {paymentsTotal > 0 ? `${((p.amount / paymentsTotal) * 100).toFixed(1)}%` : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Items card */}
          {itemsData && (
            <div className="mb-4">
              <p className="overline text-ink mb-3">Items to import</p>
              <div className="border border-ink/10 rounded-sm overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-sage/40 text-left text-ink-soft">
                    <tr>
                      <th className="px-3 py-2 overline w-10"></th>
                      <th className="px-3 py-2 overline">POS item</th>
                      <th className="px-3 py-2 overline">Map to product</th>
                      <th className="px-3 py-2 overline text-right">Qty</th>
                      <th className="px-3 py-2 overline text-right">Unit price</th>
                      <th className="px-3 py-2 overline text-right">Revenue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {itemsRows.map((r, i) => (
                      <tr
                        key={i}
                        data-testid={`pos-row-${i}`}
                        className={`border-t border-ink/5 ${r.accepted ? "bg-sage/10" : ""} ${!r.match_product_id ? "italic" : ""}`}
                      >
                        <td className="px-3 py-2">
                          <input
                            type="checkbox"
                            data-testid={`pos-row-check-${i}`}
                            className="w-4 h-4 accent-matcha-dark"
                            checked={r.accepted}
                            onChange={() => patchRow(i, { accepted: !r.accepted })}
                          />
                        </td>
                        <td className="px-3 py-2"><div className="text-ink">{r.extracted_name}</div>
                          {(r.modifiers || []).length > 0 && (
                            <details className="mt-1 group/mods">
                              <summary
                                data-testid={`pos-row-mods-${i}`}
                                className="list-none cursor-pointer text-[10px] uppercase tracking-[0.22em] text-matcha-dark hover:text-ink inline-flex items-center gap-1 select-none"
                              >
                                <span className="w-3">▸</span>
                                <span className="group-open/mods:hidden">{r.modifiers.length} modifiers · RM {Number(r.modifier_revenue || 0).toFixed(2)}</span>
                                <span className="hidden group-open/mods:inline">Hide modifiers</span>
                              </summary>
                              <ul className="mt-2 pl-4 border-l-2 border-matcha-dark/30 space-y-0.5 text-[11px] text-ink-soft">
                                {r.modifiers.map((m, mi) => (
                                  <li key={mi} className="flex items-center gap-3 leading-snug">
                                    <span className="flex-1 text-ink">{m.name}</span>
                                    <span className="font-mono text-ink-soft">×{m.qty}</span>
                                    <span className="font-mono w-20 text-right">
                                      {Number(m.revenue || 0) > 0 ? `+ RM ${Number(m.revenue).toFixed(2)}` : "—"}
                                    </span>
                                  </li>
                                ))}
                              </ul>
                            </details>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          {r.match_confidence === "fuzzy" && r.match_product_id && (
                            <div
                              className="mb-1.5 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-amber-100 border border-amber-400 text-amber-900 text-[10px] uppercase tracking-[0.16em] font-medium"
                              data-testid={`pos-row-fuzzy-badge-${i}`}
                              title={`We fuzzy-matched "${r.extracted_name}" to "${r.match_name}". Please confirm or pick a different product — clearing the dropdown reveals + Create buttons if the match is wrong. Once you publish, we'll remember this mapping for the next upload.`}
                            >
                              ⚠ Suggested — please confirm
                            </div>
                          )}
                          {r.match_confidence === "learned" && r.match_product_id && (
                            <div
                              className="mb-1.5 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-sage/60 border border-matcha-dark/40 text-matcha-dark text-[10px] uppercase tracking-[0.16em] font-medium"
                              data-testid={`pos-row-learned-badge-${i}`}
                              title={`Auto-matched "${r.extracted_name}" → "${r.match_name}" from a previous confirmation. No action needed.`}
                            >
                              ✓ Learned mapping
                            </div>
                          )}
                          <select
                            data-testid={`pos-row-map-${i}`}
                            className={inputCls + " w-full" + (r.match_confidence === "fuzzy" && r.match_product_id ? " ring-2 ring-amber-300" : "")}
                            value={r.match_product_id || ""}
                            onChange={(e) => {
                              const id = e.target.value;
                              const p = (itemsData.products_catalog || []).find((x) => x.id === id);
                              // Any explicit dropdown change is an admin confirmation,
                              // so drop the "please confirm" fuzzy badge.
                              patchRow(i, { match_product_id: id, match_name: p?.name || "", accepted: !!id, match_confidence: id ? "exact" : "none" });
                            }}
                          >
                            <option value="">— Skip / unmatched —</option>
                            {(itemsData.products_catalog || []).map((p) => (
                              <option key={p.id} value={p.id}>
                                {p.name}{p.category ? ` (${p.category})` : ""}
                              </option>
                            ))}
                          </select>
                          {!r.match_product_id && (
                            <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-1 text-[10px] uppercase tracking-[0.16em]">
                              <button
                                type="button"
                                data-testid={`pos-row-create-drink-${i}`}
                                onClick={() => quickCreateProduct(i, r, "Matcha Drinks")}
                                className="text-matcha-dark hover:text-ink underline decoration-dotted underline-offset-2"
                                title={`Create "${r.extracted_name}" as a Matcha Drinks product and auto-map this row to it.`}
                              >
                                + Create as Drink
                              </button>
                              <button
                                type="button"
                                data-testid={`pos-row-create-dessert-${i}`}
                                onClick={() => quickCreateProduct(i, r, "Desserts")}
                                className="text-matcha-dark hover:text-ink underline decoration-dotted underline-offset-2"
                                title={`Create "${r.extracted_name}" as a Desserts product and auto-map this row to it.`}
                              >
                                + Create as Dessert
                              </button>
                              <button
                                type="button"
                                data-testid={`pos-row-create-ice-cream-${i}`}
                                onClick={() => quickCreateProduct(i, r, "Ice-cream")}
                                className="text-matcha-dark hover:text-ink underline decoration-dotted underline-offset-2"
                                title={`Create "${r.extracted_name}" as an Ice-cream product and auto-map this row to it.`}
                              >
                                + Create as Ice-cream
                              </button>
                              <button
                                type="button"
                                data-testid={`pos-row-create-pastry-${i}`}
                                onClick={() => quickCreateProduct(i, r, "Pastry")}
                                className="text-matcha-dark hover:text-ink underline decoration-dotted underline-offset-2"
                                title={`Create "${r.extracted_name}" as a Pastry product and auto-map this row to it.`}
                              >
                                + Create as Pastry
                              </button>
                              {r.suggested_category &&
                                r.suggested_category !== "Matcha Drinks" &&
                                r.suggested_category !== "Desserts" &&
                                r.suggested_category !== "Ice-cream" &&
                                r.suggested_category !== "Pastry" && (
                                  <button
                                    type="button"
                                    data-testid={`pos-row-create-suggested-${i}`}
                                    onClick={() => quickCreateProduct(i, r, r.suggested_category)}
                                    className="text-matcha-dark hover:text-ink underline decoration-dotted underline-offset-2"
                                    title={`Create "${r.extracted_name}" as a ${r.suggested_category} product.`}
                                  >
                                    + Create as {r.suggested_category}
                                  </button>
                                )}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right whitespace-nowrap">
                          <input
                            data-testid={`pos-row-qty-${i}`}
                            type="number" step="1" min="0"
                            className={inputCls + " w-20 text-right"}
                            value={r.qty}
                            onChange={(e) => patchRow(i, { qty: e.target.value })}
                          />
                        </td>
                        <td className="px-3 py-2 text-right whitespace-nowrap">
                          <input
                            data-testid={`pos-row-price-${i}`}
                            type="number" step="0.01" min="0"
                            className={inputCls + " w-24 text-right"}
                            value={r.unit_price}
                            onChange={(e) => patchRow(i, { unit_price: e.target.value })}
                          />
                        </td>
                        <td className="px-3 py-2 text-right font-mono text-ink-soft whitespace-nowrap">{fmt(r.revenue)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-3 mt-5">
            <button
              type="button"
              data-testid="pos-back"
              onClick={reset}
              className="text-xs uppercase tracking-[0.18em] text-ink-soft hover:text-ink"
            >
              ← Start over
            </button>
            <button
              type="button"
              data-testid="pos-commit-btn"
              disabled={!canCommit}
              onClick={commit}
              className="inline-flex items-center gap-2 rounded-full px-6 py-2 bg-ink text-bone text-xs uppercase tracking-[0.22em] hover:bg-matcha-dark transition-colors disabled:opacity-50"
            >
              <CheckIcon size={14} /> {lastAutoSave ? "Re-import (apply edits)" : "Import to Matcha Story"}
            </button>
          </div>
        </div>
      )}

      {phase === "committing" && (
        <div className="bg-bone border border-ink/10 rounded-sm py-16 text-center" data-testid="pos-committing">
          <Loader2 size={36} className="mx-auto mb-4 text-matcha-dark animate-spin" />
          <p className="text-ink text-sm">Importing sales…</p>
        </div>
      )}

      {phase === "done" && result && (
        <div className="bg-bone border border-matcha-dark/40 rounded-sm p-6" data-testid="pos-done">
          <div className="flex items-center gap-3 mb-4">
            <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-matcha-dark text-bone">
              <CheckIcon size={18} />
            </span>
            <div>
              <p className="text-ink text-base">Import complete</p>
              <p className="text-[11px] uppercase tracking-[0.22em] text-ink-soft">
                {result.replaced_previous ? "Any prior import / quick total for this date was replaced" : "New sale created"}
              </p>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
            <Stat label="Date" value={result.sale?.date || date} />
            <Stat label="Transactions" value={String(result.sale?.order_count || 0)} />
            <Stat label="Revenue" value={fmt(result.sale?.total || 0)} />
            <Stat label="Gross profit" value={fmt(result.sale?.gross_profit || 0)} />
          </div>
          <p
            className="text-[11px] uppercase tracking-[0.22em] text-matcha-dark border-t border-matcha-dark/20 pt-3 mb-4"
            data-testid="pos-done-no-products-added"
          >
            ✓ Products & Inventory untouched — {(result.auto_created_products || []).length} new product{(result.auto_created_products || []).length === 1 ? "" : "s"} added
          </p>
          <button
            type="button"
            data-testid="pos-import-more"
            onClick={reset}
            className="inline-flex items-center gap-2 rounded-full px-6 py-2 bg-ink text-bone text-xs uppercase tracking-[0.22em] hover:bg-matcha-dark transition-colors"
          >
            Import another day
          </button>
        </div>
      )}

      {phase === "pick" && !itemsData && !paymentsData && !ordersData && (
        <p className="text-[11px] text-ink-soft border-t border-ink/10 pt-4">
          <strong className="text-ink">How to export from GEBME:</strong> Merchant Portal → Reports → pick a date →
          Export any of <em>Store Itemized</em>, <em>Payment Received</em>, <em>Daily Order Overview</em> or
          <em> Store Itemized Breakdown</em> to Excel. Drop them all in the box above at once — the order
          doesn&apos;t matter.
        </p>
      )}

      {/* Historical browser — always visible so admin can pick any past date */}
      <PosHistory key={lastAutoSave ? lastAutoSave.getTime() : "init"} />
    </div>
  );
}

function MultiDropZone({ onFiles, uploading, files }) {
  const [dragOver, setDragOver] = useState(false);
  const inputRef = React.useRef(null);
  const handleDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    const dropped = Array.from(e.dataTransfer.files || []);
    if (dropped.length) onFiles(dropped);
  };
  return (
    <div data-testid="pos-dropzone">
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
        className={`rounded-sm p-8 text-center cursor-pointer transition-colors border-2 border-dashed
          ${dragOver ? "border-matcha-dark bg-sage/20" : "border-ink/25 hover:border-matcha-dark bg-bone"}`}
      >
        <FileSpreadsheet size={40} className="mx-auto mb-3 text-ink-soft" />
        <p className="text-ink text-base font-medium mb-1">
          {uploading ? "Parsing…" : "Drop your GEBME reports here"}
        </p>
        <p className="text-[11px] uppercase tracking-[0.22em] text-ink-soft">
          Drag one, two, three or all four .xlsx files at once · Max 12&nbsp;MB each · Click to browse
        </p>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept=".xlsx,.xls,.xlsm"
          className="hidden"
          onChange={(e) => {
            const picked = Array.from(e.target.files || []);
            e.target.value = "";
            if (picked.length) onFiles(picked);
          }}
          data-testid="pos-dropzone-input"
        />
      </div>

      {/* Chips for uploaded files */}
      {files.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2" data-testid="pos-file-chips">
          {files.map((f) => (
            <div
              key={f.kind}
              data-testid={`pos-file-chip-${f.kind}`}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-sage/30 border border-matcha-dark/30 text-xs text-ink"
            >
              <CheckIcon size={12} className="text-matcha-dark" />
              <span className="font-medium">{f.name}</span>
              <span className="text-ink-soft text-[11px]">· {f.summary}</span>
              <button
                type="button"
                onClick={f.onClear}
                className="ml-1 text-ink-soft hover:text-red-700"
                aria-label={`Remove ${f.name}`}
              >
                <X size={12} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}




function Stat({ label, value, sub }) {
  return (
    <div>
      <p className="overline text-ink-soft mb-1">{label}</p>
      <p className="text-ink text-lg leading-tight">{value}</p>
      {sub && <p className="text-[10px] text-ink-soft uppercase tracking-[0.18em] mt-1">{sub}</p>}
    </div>
  );
}

/**
 * Historical browser for POS-imported daily sales. Admin can:
 *  - pick a single date (jumps range to that one day)
 *  - pick a date range (default: last 30 days)
 * Shows: aggregate KPIs, per-day list (click to expand?), payment breakdown,
 * hourly chart, top items.
 */
function PosHistory() {
  const today = new Date().toISOString().slice(0, 10);
  const monthAgo = (() => {
    const d = new Date(); d.setDate(d.getDate() - 29);
    return d.toISOString().slice(0, 10);
  })();
  const [from, setFrom] = useState(monthAgo);
  const [to, setTo] = useState(today);
  const [mode, setMode] = useState("range"); // "single" | "range"
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [expandedDate, setExpandedDate] = useState(null);

  const load = async () => {
    setErr(""); setLoading(true);
    try {
      const url = mode === "single"
        ? `/mgmt/sales/import-pos/history?date_from=${to}&date_to=${to}`
        : `/mgmt/sales/import-pos/history?date_from=${from}&date_to=${to}`;
      const { data: d } = await api.get(url);
      setData(d);
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [from, to, mode]);

  const removeDay = async (id) => {
    if (!window.confirm("Revert this imported day? The aggregate sale for this date will be deleted.")) return;
    await api.delete(`/mgmt/sales/import-pos/${id}`);
    load();
  };

  // Wipes every sale in the currently-picked range — POS imports AND manual
  // quick daily totals AND itemized entries. Gives the admin a true clean
  // slate before re-dropping the GEBME files.
  const [erasing, setErasing] = useState(false);
  const eraseRange = async () => {
    const rangeFrom = mode === "single" ? to : from;
    const rangeTo = to;
    const label = rangeFrom === rangeTo ? rangeFrom : `${rangeFrom} → ${rangeTo}`;
    const confirmMsg =
      `Erase ALL sales for ${label}?\n\n` +
      `Deletes every sales row in this range — POS imports, quick daily totals, and manual itemized entries. ` +
      `Use this before re-dropping GEBME files if the numbers look wrong. This cannot be undone.`;
    if (!window.confirm(confirmMsg)) return;
    setErasing(true);
    try {
      const { data: res } = await api.delete(
        `/mgmt/sales/import-pos?date_from=${rangeFrom}&date_to=${rangeTo}`
      );
      window.alert(`Erased ${res.deleted} sales row${res.deleted === 1 ? "" : "s"} for ${label}.`);
      await load();
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail) || e.message);
    } finally {
      setErasing(false);
    }
  };

  return (
    <div className="mt-10 pt-8 border-t border-ink/10" data-testid="pos-history">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-5">
        <div>
          <p className="overline text-matcha-dark flex items-center gap-2">
            <Calendar size={12} /> History
          </p>
          <h3 className="font-display text-xl text-ink" style={{ letterSpacing: "0.02em" }}>
            Browse imported daily sales
          </h3>
          <p className="text-ink-soft text-sm mt-1">
            Pick a single day or a date range to see everything that&apos;s been synced from your POS.
          </p>
        </div>
        <div className="inline-flex rounded-full border border-ink/15 overflow-hidden text-[11px] uppercase tracking-[0.22em]">
          <button
            type="button"
            data-testid="pos-history-mode-range"
            onClick={() => setMode("range")}
            className={`px-4 py-2 transition-colors ${mode === "range" ? "bg-ink text-bone" : "text-ink-soft hover:text-ink bg-bone"}`}
          >
            Date range
          </button>
          <button
            type="button"
            data-testid="pos-history-mode-single"
            onClick={() => setMode("single")}
            className={`px-4 py-2 transition-colors ${mode === "single" ? "bg-ink text-bone" : "text-ink-soft hover:text-ink bg-bone"}`}
          >
            Single date
          </button>
        </div>
      </div>

      {/* Date pickers */}
      <div className="flex flex-wrap items-end gap-3 mb-6 bg-bone border border-ink/10 rounded-sm p-4">
        {mode === "range" && (
          <div>
            <label className="overline text-ink-soft block mb-1">From</label>
            <input
              type="date"
              data-testid="pos-history-from"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className={inputCls}
            />
          </div>
        )}
        <div>
          <label className="overline text-ink-soft block mb-1">{mode === "single" ? "Date" : "To"}</label>
          <input
            type="date"
            data-testid="pos-history-to"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className={inputCls}
          />
        </div>
        <div className="text-[11px] uppercase tracking-[0.22em] text-ink-soft ml-2 mb-2">
          {loading ? <span className="inline-flex items-center gap-1"><Loader2 size={12} className="animate-spin" /> Loading…</span> :
            data && <span><strong className="text-ink">{data.totals.days}</strong> day{data.totals.days === 1 ? "" : "s"} imported</span>}
        </div>
        <div className="ml-auto mb-1">
          <button
            type="button"
            data-testid="pos-history-erase"
            onClick={eraseRange}
            disabled={erasing || loading}
            className="inline-flex items-center gap-2 rounded-full border border-red-600/30 bg-bone px-4 py-2 text-[11px] uppercase tracking-[0.22em] text-red-700 transition-colors hover:bg-red-600 hover:text-bone hover:border-red-600 disabled:opacity-40 disabled:hover:bg-bone disabled:hover:text-red-700 disabled:cursor-not-allowed"
            title={mode === "single" ? `Erase POS data for ${to}` : `Erase POS data from ${from} to ${to}`}
          >
            {erasing ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
            {erasing ? "Erasing…" : mode === "single" ? "Erase this date" : "Erase this range"}
          </button>
        </div>
      </div>

      {err && <p className="text-sm text-red-700 bg-red-50 px-3 py-2 rounded-sm mb-4">{err}</p>}

      {data && (
        <>
          {/* Range totals */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            <RangeStat label="Revenue" value={fmt(data.totals.revenue)} sub={`${data.totals.days} day${data.totals.days === 1 ? "" : "s"} synced`} />
            <RangeStat label="Transactions" value={String(data.totals.orders)} sub={data.totals.days ? `${(data.totals.orders / data.totals.days).toFixed(1)} / day avg` : "—"} />
            <RangeStat label="AOV" value={fmt(data.totals.aov)} sub="per transaction" highlight />
            <RangeStat label="Peak hour" value={data.totals.peak_hour !== null ? `${String(data.totals.peak_hour).padStart(2,'0')}:00` : "—"} sub="busiest of range" />
            <RangeStat label="Dine-in / Take-away"
              value={`${data.channels.dine_in.transactions} / ${data.channels.take_away.transactions}`}
              sub={`RM ${data.channels.dine_in.amount.toFixed(0)} / RM ${data.channels.take_away.amount.toFixed(0)}`} />
          </div>

          {/* Hourly chart across the range */}
          {data.hourly.length > 0 && (
            <div className="rounded-sm border border-ink/10 bg-bone p-4 mb-6">
              <p className="overline text-ink-soft mb-3">Revenue by hour — {data.date_from} → {data.date_to}</p>
              <HourlyLineChart hourly={data.hourly} peakHour={data.totals.peak_hour} />
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            {/* Payments */}
            <div className="rounded-sm border border-ink/10 bg-bone p-4">
              <p className="overline text-ink-soft mb-3">By payment method</p>
              {data.by_payment.length === 0 ? (
                <p className="text-sm text-ink-soft italic">No payment data in this range.</p>
              ) : (
                <ul className="space-y-2 text-sm" data-testid="pos-history-payments">
                  {data.by_payment.map((p) => {
                    const totalRev = data.totals.revenue || 1;
                    return (
                      <li key={p.method} className="border-b border-ink/5 pb-2">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-ink">{p.method}</span>
                          <span className="text-ink font-mono">{fmt(p.amount)}</span>
                        </div>
                        <div className="h-1 bg-ink/5 rounded-full overflow-hidden mb-1">
                          <div className="h-full bg-matcha-dark" style={{ width: `${(p.amount / totalRev) * 100}%` }} />
                        </div>
                        <p className="text-[10px] text-ink-soft uppercase tracking-[0.18em]">
                          {p.transactions} txns · AOV {fmt(p.aov)} · {((p.amount / totalRev) * 100).toFixed(1)}%
                        </p>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            {/* Top items */}
            <div className="rounded-sm border border-ink/10 bg-bone p-4">
              <p className="overline text-ink-soft mb-3">Top items</p>
              {data.top_items.length === 0 ? (
                <p className="text-sm text-ink-soft italic">
                  No item-level data yet — upload the Store Itemized report.
                </p>
              ) : (
                <ol className="space-y-2 text-sm" data-testid="pos-history-top-items">
                  {data.top_items.map((t, i) => (
                    <li key={i} className="flex items-center justify-between border-b border-ink/5 pb-2">
                      <span className="text-ink">
                        <span className="text-matcha-dark mr-2 font-mono">{String(i + 1).padStart(2, "0")}</span>
                        {t.name}
                        <span className="text-[10px] text-ink-soft uppercase tracking-[0.18em] ml-2">
                          {t.qty} sold
                        </span>
                      </span>
                      <span className="text-ink font-mono">{fmt(t.revenue)}</span>
                    </li>
                  ))}
                </ol>
              )}
            </div>
          </div>

          {/* Modifiers breakdown */}
          {(data.modifier_groups?.length || 0) > 0 && (
            <div className="rounded-sm border border-ink/10 bg-bone p-4 mb-6" data-testid="pos-history-modifiers">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="overline text-ink-soft">Modifier breakdown</p>
                  <p className="text-[11px] text-ink-soft mt-1">
                    <strong className="text-ink">{data.totals.modifier_qty}</strong> modifier selections across the range · shows what customers actually customise
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {data.modifier_groups.map((g) => {
                  const gTotal = g.qty || 1;
                  return (
                    <div key={g.group} className="border border-ink/10 rounded-sm p-4">
                      <div className="flex items-baseline justify-between mb-2">
                        <p className="overline text-matcha-dark">{g.group}</p>
                        <p className="text-[11px] text-ink-soft">
                          <span className="text-ink font-mono">{g.qty}</span> × ·
                          {g.revenue > 0 ? <span className="text-matcha-dark ml-1">{fmt(g.revenue)}</span> : ""}
                        </p>
                      </div>
                      <ul className="space-y-1.5">
                        {g.items.map((m) => (
                          <li key={m.name} className="text-sm">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-ink">{m.name}</span>
                              <span className="font-mono text-ink-soft">
                                <span className="text-ink">×{m.qty}</span>
                                {m.revenue > 0 && <span className="ml-2 text-matcha-dark">+{fmt(m.revenue)}</span>}
                              </span>
                            </div>
                            <div className="h-1 bg-ink/5 rounded-full overflow-hidden">
                              <div className="h-full bg-matcha-dark/70" style={{ width: `${(m.qty / gTotal) * 100}%` }} />
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Per-day list */}
          <div className="rounded-sm border border-ink/10 bg-bone p-4">
            <p className="overline text-ink-soft mb-3">
              <TrendingUp size={12} className="inline mr-1" />
              Daily records ({data.days.length})
            </p>
            {data.days.length === 0 ? (
              <div className="py-10 text-center">
                <p className="text-ink-soft text-sm mb-1">Nothing imported for this range yet.</p>
                <p className="text-[11px] text-ink-soft uppercase tracking-[0.22em]">
                  Drop a GEBME export above to add data
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="pos-history-table">
                  <thead className="text-left text-ink-soft">
                    <tr>
                      <th className="px-3 py-2 overline">Date</th>
                      <th className="px-3 py-2 overline text-right">Txns</th>
                      <th className="px-3 py-2 overline text-right">Revenue</th>
                      <th className="px-3 py-2 overline text-right">AOV</th>
                      <th className="px-3 py-2 overline">Peak</th>
                      <th className="px-3 py-2 overline">Payments</th>
                      <th className="px-3 py-2 overline text-right">Items · Mods</th>
                      <th className="px-3 py-2 overline w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.days.map((day) => (
                      <React.Fragment key={day.id}>
                        <tr className={`border-t border-ink/5 cursor-pointer ${expandedDate === day.date ? "bg-sage/10" : "hover:bg-ink/[0.02]"}`}
                          onClick={() => setExpandedDate(expandedDate === day.date ? null : day.date)}
                          data-testid={`pos-history-row-${day.date}`}
                        >
                          <td className="px-3 py-2 text-ink whitespace-nowrap">
                            <span className="text-matcha-dark mr-1.5">{expandedDate === day.date ? "▾" : "▸"}</span>
                            {day.date}
                          </td>
                          <td className="px-3 py-2 text-right font-mono">{day.order_count}</td>
                          <td className="px-3 py-2 text-right font-mono">{fmt(day.revenue)}</td>
                          <td className="px-3 py-2 text-right font-mono text-matcha-dark">{fmt(day.aov)}</td>
                          <td className="px-3 py-2 text-ink-soft whitespace-nowrap">
                            {day.peak_hour !== null && day.peak_hour !== undefined ? `${String(day.peak_hour).padStart(2,'0')}:00` : "—"}
                          </td>
                          <td className="px-3 py-2 text-[11px] text-ink-soft">
                            {day.payment_methods && day.payment_methods.length
                              ? day.payment_methods.slice(0, 4).join(" · ") + (day.payment_methods.length > 4 ? "…" : "")
                              : "—"}
                          </td>
                          <td className="px-3 py-2 text-right font-mono text-ink-soft">
                            {day.items_count}{day.modifier_count ? ` · ${day.modifier_count}` : ""}
                          </td>
                          <td className="px-3 py-2 text-right">
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); removeDay(day.id); }}
                              data-testid={`pos-history-delete-${day.date}`}
                              className="text-ink-soft hover:text-red-700"
                              title="Revert this import"
                            >
                              <Trash2 size={14} />
                            </button>
                          </td>
                        </tr>
                        {expandedDate === day.date && (
                          <tr className="bg-cream/40" data-testid={`pos-history-detail-${day.date}`}>
                            <td colSpan={8} className="px-3 py-5">
                              {/* Compact day summary strip */}
                              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-5 pb-4 border-b border-ink/10">
                                <div>
                                  <p className="overline text-ink-soft mb-0.5">Revenue</p>
                                  <p className="text-ink text-lg leading-tight">{fmt(day.revenue)}</p>
                                </div>
                                <div>
                                  <p className="overline text-ink-soft mb-0.5">Transactions</p>
                                  <p className="text-ink text-lg leading-tight">{day.order_count}</p>
                                </div>
                                <div>
                                  <p className="overline text-matcha-dark mb-0.5">AOV</p>
                                  <p className="text-matcha-dark text-lg leading-tight">{fmt(day.aov)}</p>
                                </div>
                                <div>
                                  <p className="overline text-ink-soft mb-0.5">Items · Modifiers</p>
                                  <p className="text-ink text-lg leading-tight">
                                    {day.items_count} <span className="text-ink-soft">·</span> {day.modifier_count}
                                  </p>
                                </div>
                                <div>
                                  <p className="overline text-ink-soft mb-0.5">Dine-in / Take-away</p>
                                  <p className="text-ink text-lg leading-tight">
                                    {day.channels?.dine_in?.transactions || 0} <span className="text-ink-soft">/</span> {day.channels?.take_away?.transactions || 0}
                                  </p>
                                </div>
                              </div>

                              {(day.items || []).length === 0 ? (
                                <p className="text-sm text-ink-soft italic">
                                  No item-level data for this day — upload the Store Itemized report to see items &amp; modifiers.
                                </p>
                              ) : (
                                <div className="space-y-3">
                                  <div className="flex items-baseline justify-between gap-3 flex-wrap">
                                    <p className="overline text-ink">Items sold · click any item to expand modifiers</p>
                                    <p className="text-[11px] text-ink-soft uppercase tracking-[0.18em]">
                                      Sorted by revenue
                                    </p>
                                  </div>
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    {[...day.items].sort((a, b) => b.line_revenue - a.line_revenue).map((it, idx) => (
                                      <details
                                        key={idx}
                                        className="border border-ink/10 rounded-sm bg-bone group open:bg-sage/10"
                                        data-testid={`pos-day-item-${day.date}-${idx}`}
                                      >
                                        <summary className="cursor-pointer list-none p-3 flex items-center justify-between gap-2">
                                          <div className="flex-1 min-w-0">
                                            <p className="text-ink text-sm font-medium truncate">
                                              <span className="text-matcha-dark mr-1.5 group-open:hidden">▸</span>
                                              <span className="text-matcha-dark mr-1.5 hidden group-open:inline">▾</span>
                                              {it.name}
                                            </p>
                                            <p className="text-[11px] text-ink-soft uppercase tracking-[0.18em] mt-0.5 ml-4">
                                              {it.qty} × RM {it.unit_price.toFixed(2)}
                                              {it.category ? ` · ${it.category}` : ""}
                                              {(it.modifiers || []).length > 0 && (
                                                <span className="ml-2 text-matcha-dark normal-case tracking-normal">
                                                  · {it.modifiers.length} modifier{it.modifiers.length === 1 ? "" : "s"}
                                                </span>
                                              )}
                                            </p>
                                          </div>
                                          <p className="text-ink font-mono text-sm whitespace-nowrap">{fmt(it.line_revenue)}</p>
                                        </summary>
                                        {(it.modifiers || []).length > 0 ? (
                                          <ul className="px-3 pb-3 ml-4 pl-3 border-l-2 border-matcha-dark/30 space-y-1" data-testid={`pos-day-item-mods-${day.date}-${idx}`}>
                                            {it.modifiers.map((m, mi) => (
                                              <li key={mi} className="text-[12px] flex items-center justify-between gap-3">
                                                <span className="text-ink flex-1">{m.name}</span>
                                                <span className="font-mono text-ink-soft">
                                                  ×{m.qty}
                                                  {m.revenue > 0 && <span className="text-matcha-dark ml-2">+RM {m.revenue.toFixed(2)}</span>}
                                                </span>
                                              </li>
                                            ))}
                                          </ul>
                                        ) : (
                                          <div
                                            className="px-3 pb-3 ml-4 pl-3 border-l-2 border-ink/10"
                                            data-testid={`pos-day-item-no-mods-${day.date}-${idx}`}
                                          >
                                            <p className="text-[12px] text-ink-soft italic">
                                              No modifiers for this item.
                                            </p>
                                            <p className="text-[11px] text-ink-soft/70 mt-1">
                                              Upload the <strong className="text-ink">Store Category Breakdown</strong> export to see per-item modifiers (Upsize, Cold Whisk, etc.).
                                            </p>
                                          </div>
                                        )}
                                      </details>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function RangeStat({ label, value, sub, highlight }) {
  return (
    <div className={`rounded-sm border p-4 ${highlight ? "bg-sage/15 border-matcha-dark/40" : "bg-bone border-ink/10"}`}>
      <p className="overline text-ink-soft mb-1">{label}</p>
      <p className={`font-display text-2xl leading-tight ${highlight ? "text-matcha-dark" : "text-ink"}`} style={{ letterSpacing: "0.02em" }}>
        {value}
      </p>
      {sub && <p className="text-[10px] text-ink-soft uppercase tracking-[0.18em] mt-1">{sub}</p>}
    </div>
  );
}

/**
 * Dotted line chart of revenue over the day.
 * - Editorial matcha aesthetic: thin dashed line, filled dots on data points, a slightly larger
 *   "halo" dot on the peak hour, subtle horizontal gridlines.
 * - Pure inline SVG — no chart library, no Tailwind theme dependencies.
 */
function HourlyLineChart({ hourly, peakHour }) {
  const data = (hourly || []).slice().sort((a, b) => a.hour - b.hour);
  if (data.length === 0) return null;

  const W = 800;            // logical viewBox width
  const H = 200;            // logical viewBox height
  const PAD_L = 44;         // room for y-axis labels
  const PAD_R = 12;
  const PAD_T = 18;
  const PAD_B = 26;         // room for x-axis labels

  const maxAmount = Math.max(1, ...data.map((h) => Number(h.total_amount || 0)));
  // Round the y-axis top to a nice number (nearest 100 up to 500, then nearest 500, etc.)
  const step = maxAmount < 100 ? 20 : maxAmount < 300 ? 50 : maxAmount < 800 ? 100 : 200;
  const yMax = Math.ceil(maxAmount / step) * step;
  const yTicks = 4;
  const tickValues = Array.from({ length: yTicks + 1 }, (_, i) => (yMax / yTicks) * i);

  const innerW = W - PAD_L - PAD_R;
  const innerH = H - PAD_T - PAD_B;

  const xOf = (i) => PAD_L + (data.length === 1 ? innerW / 2 : (i / (data.length - 1)) * innerW);
  const yOf = (v) => PAD_T + innerH - (Number(v || 0) / yMax) * innerH;

  const points = data.map((d, i) => ({ ...d, x: xOf(i), y: yOf(d.total_amount) }));
  const pathD = points.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(" ");

  return (
    <div className="relative" data-testid="pos-hourly-chart">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        width="100%"
        height="220"
        preserveAspectRatio="none"
        role="img"
        aria-label="Revenue by hour"
      >
        {/* Horizontal gridlines + y-axis labels */}
        {tickValues.map((v, i) => {
          const y = yOf(v);
          return (
            <g key={i}>
              <line
                x1={PAD_L} x2={W - PAD_R} y1={y} y2={y}
                stroke="#2C3326" strokeOpacity="0.08" strokeWidth="1"
              />
              <text
                x={PAD_L - 8} y={y + 3}
                textAnchor="end"
                fontSize="9"
                fill="#6B715F"
                fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace"
              >
                {v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v.toFixed(0)}
              </text>
            </g>
          );
        })}

        {/* Dotted line connecting the hours */}
        <path
          d={pathD}
          fill="none"
          stroke="#5A6B3E"
          strokeWidth="1.6"
          strokeDasharray="3 4"
          strokeLinecap="round"
          strokeLinejoin="round"
        />

        {/* Data points */}
        {points.map((p) => {
          const isPeak = peakHour === p.hour;
          return (
            <g key={p.hour} className="group">
              {isPeak && (
                <circle cx={p.x} cy={p.y} r="8" fill="#5A6B3E" opacity="0.12" />
              )}
              <circle
                cx={p.x} cy={p.y}
                r={isPeak ? 4.5 : 3}
                fill={isPeak ? "#2C3326" : "#5A6B3E"}
                stroke="#F9F9F6"
                strokeWidth="1.5"
                data-testid={`pos-hourly-${p.hour}`}
              >
                <title>
                  {`${String(p.hour).padStart(2, "0")}:00 · RM ${Number(p.total_amount || 0).toFixed(2)} · ${p.total_txns} txns`}
                </title>
              </circle>
              {isPeak && (
                <text
                  x={p.x} y={p.y - 12}
                  textAnchor="middle"
                  fontSize="9"
                  fill="#2C3326"
                  fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace"
                >
                  RM {Number(p.total_amount || 0).toFixed(0)}
                </text>
              )}
            </g>
          );
        })}

        {/* X-axis baseline */}
        <line
          x1={PAD_L} x2={W - PAD_R}
          y1={PAD_T + innerH} y2={PAD_T + innerH}
          stroke="#2C3326" strokeOpacity="0.18"
        />

        {/* X-axis hour labels */}
        {points.map((p) => (
          <text
            key={p.hour}
            x={p.x}
            y={PAD_T + innerH + 14}
            textAnchor="middle"
            fontSize="9"
            fill={peakHour === p.hour ? "#2C3326" : "#6B715F"}
            fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace"
            fontWeight={peakHour === p.hour ? 600 : 400}
          >
            {String(p.hour).padStart(2, "0")}
          </text>
        ))}
      </svg>
    </div>
  );
}

